import {
  ApiError,
  apiGet,
  apiGetPaginated,
  apiPost,
  apiPut,
  apiPatch,
  apiDelete,
  buildApiUrl,
} from './client';
import type { ApiResponse, OrganizationRole, PaginatedResult } from '@/types';

export interface UserOrganizationRef {
  id: string;
  name: string;
  slug: string;
  role: OrganizationRole;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  picture: string | null;
  emailVerified: boolean;
  isSuperAdmin: boolean;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  organization?: UserOrganizationRef | null;
}

export interface CreateUserInput {
  email: string;
  name: string;
  password?: string;
  isSuperAdmin?: boolean;
  emailVerified?: boolean;
  organizationId?: string;
  organizationRole?: OrganizationRole;
}

export interface CreateUserResult extends AdminUser {
  // Present only when the server generated a temporary password (shown once).
  temporaryPassword?: string | null;
}

export interface UpdateUserInput {
  name?: string;
  email?: string;
  picture?: string | null;
  emailVerified?: boolean;
  isActive?: boolean;
  isSuperAdmin?: boolean;
  password?: string;
}

export interface UserAppAccess {
  productId: string;
  productName: string;
  productSlug: string;
  organizationId: string;
  organizationName: string | null;
  roles: string[];
  permissions: string[];
  grantedAt: string;
  expiresAt: string | null;
  isActive: boolean;
}

export interface UserStats {
  total: number;
  active: number;
  disabled: number;
  unverified: number;
  superAdmins: number;
}

export interface UserSessionInfo {
  id: string;
  userId: string;
  createdAt: string;
  expiresAt: string;
  lastActivityAt: string;
  userAgent: string | null;
  ipAddress: string | null;
  deviceInfo: { browser: string; os: string; device: string } | null;
  location: string | null;
  isCurrent: boolean;
}

export interface UsersListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  isActive?: boolean;
  emailVerified?: boolean;
  isSuperAdmin?: boolean;
  /** Organization id, or 'none' */
  organizationId?: string;
}

export type UserImportMode = 'skip' | 'update';

export interface UserImportResultRow {
  row: number;
  email: string;
  status: 'created' | 'updated' | 'skipped' | 'error';
  message?: string;
  temporaryPassword?: string | null;
}

export interface UserImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  rows: UserImportResultRow[];
}

async function downloadCsv(endpoint: string): Promise<Blob> {
  const response = await fetch(buildApiUrl(endpoint), {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    await throwApiError(response);
  }

  return response.blob();
}

async function throwApiError(response: Response): Promise<never> {
  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json')) {
    const data = (await response.json()) as ApiResponse<unknown>;
    const message = typeof data.error === 'string' ? data.error : data.error?.message;
    const code = typeof data.error === 'string' ? undefined : data.error?.code;
    const details = typeof data.error === 'string' ? undefined : data.error?.details;
    throw new ApiError(
      message || 'Request failed',
      code || 'UNKNOWN_ERROR',
      response.status,
      details
    );
  }

  throw new ApiError('Request failed', 'NETWORK_ERROR', response.status);
}

export const usersApi = {
  listPage: (query: UsersListQuery = {}): Promise<PaginatedResult<AdminUser[]>> =>
    apiGetPaginated<AdminUser[]>('/api/users', { params: { ...query } }),

  stats: () => apiGet<UserStats>('/api/users/stats'),

  resendVerification: (id: string) =>
    apiPost<{ message: string }>(`/api/users/${id}/resend-verification`),

  forceReset: (id: string) =>
    apiPost<{ message: string; sessionsRevoked: number }>(`/api/users/${id}/force-reset`),

  listSessions: (id: string) => apiGet<UserSessionInfo[]>(`/api/users/${id}/sessions`),

  revokeSession: (id: string, sessionId: string) =>
    apiDelete<{ revokedCount: number }>(`/api/users/${id}/sessions/${sessionId}`),

  revokeAllSessions: (id: string) => apiDelete<{ revokedCount: number }>(`/api/users/${id}/sessions`),

  // Self-service
  ownedOrganizations: () =>
    apiGet<{ id: string; name: string; slug: string }[]>('/api/users/me/owned-organizations'),

  deleteSelf: (confirmEmail: string) =>
    apiDelete<void>('/api/users/me', { body: JSON.stringify({ confirmEmail }) }),

  list: async () => (await usersApi.listPage({ pageSize: 100 })).data,

  get: (id: string) => apiGet<AdminUser>(`/api/users/${id}`),

  create: (data: CreateUserInput) => apiPost<CreateUserResult>('/api/users', data),

  update: (id: string, data: UpdateUserInput) => apiPatch<AdminUser>(`/api/users/${id}`, data),

  remove: (id: string) => apiDelete<void>(`/api/users/${id}`),

  // Organization (single-org model)
  getOrganization: (id: string) =>
    apiGet<UserOrganizationRef | null>(`/api/users/${id}/organization`),

  setOrganization: (id: string, organizationId: string, role: OrganizationRole = 'member') =>
    apiPut<UserOrganizationRef>(`/api/users/${id}/organization`, { organizationId, role }),

  removeOrganization: (id: string) => apiDelete<void>(`/api/users/${id}/organization`),

  // App (product) access
  listProducts: (id: string) => apiGet<UserAppAccess[]>(`/api/users/${id}/products`),

  downloadImportTemplate: () => downloadCsv('/api/users/import-template.csv'),

  exportCsv: () => downloadCsv('/api/users/export.csv'),

  importCsv: async (csv: string, mode: UserImportMode) => {
    const response = await fetch(buildApiUrl('/api/users/import.csv', { mode }), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'text/csv' },
      body: csv,
    });

    if (!response.ok) {
      await throwApiError(response);
    }

    const data = (await response.json()) as ApiResponse<UserImportResult>;
    if (!data.success || !data.data) {
      const message = typeof data.error === 'string' ? data.error : data.error?.message;
      const code = typeof data.error === 'string' ? undefined : data.error?.code;
      const details = typeof data.error === 'string' ? undefined : data.error?.details;
      throw new ApiError(
        message || 'Import failed',
        code || 'UNKNOWN_ERROR',
        response.status,
        details
      );
    }

    return data.data;
  },
};
